import React, {
    useRef, useState, useCallback, useEffect,
    createContext, useContext,
    forwardRef
} from 'react'
import { useIntl } from 'react-intl'
import { modalify } from 'utilities/modal'
import { cls } from 'utilities/dom'
import FocusLock from 'react-focus-lock'
import { ModalErrorBoundary } from 'components/error-boundary'
import {
    Wrapper, Container, Content,
    ModalHeader, CloseButton, Kicker, Huge, Big, Medium, Small, Caption,
    ModalBody, ModalFooter,
    LoadingContainer
} from './s'
import Prompt from 'prompts/standard'
import Symbol from 'components/symbol'
import Loader from 'components/loader'
import TextOrCustomElement from './text-or-custom'

const ModalContext = createContext()
ModalContext.displayName = 'Modal'

const Modal = ({ children, contentWrapper: ContentWrapper = Content, salt, ...props }) => {
    const { formatMessage } = useIntl()

    const dialog = useRef()

    const {
        show = false,
        duration = [300, 50],
        dismiss,
        dismissOnOutsideClick = false,
        contentClassName,
        ...modal
    } = props

    const [showing, setShowing] = useState(show)
    const [dirty, setDirty] = useState(false)
    const [prompting, setPrompting] = useState(false)
    const [options, setOptions] = useState({})

    const handleOpen = useCallback(() => {
        setShowing(true)
        dialog.current?.showModal()
    }, [])

    const handleClose = useCallback((...args) => {
        const [options = {}] = [...args].filter(argument => !(argument instanceof Event))

        const {
            force = false,
            native = false
        } = options

        const soft = !!dialog.current?._temporarilyHidden

        if(!dirty || !!force) {
            !soft && dismiss()
            !!force && setDirty(false)

            if(!native) {
                dialog.current.addEventListener('animationend', () => setShowing(false), { once: true })
                dialog.current.close()
            }
        } else {
            setPrompting(true)
        }
    }, [dirty])

    const handleOutsideClick = useCallback(event => {
        if(!!dismissOnOutsideClick && event.target.parentNode.contains(dialog.current)) {
            handleClose()
        }
    }, [dismissOnOutsideClick, handleClose])

    useEffect(() => {
        if(dialog.current) {
            const controller = new AbortController()

            dialog.current.addEventListener('open', handleOpen, controller)
            dialog.current.addEventListener('close', () => handleClose({ native: true }), controller)
            dialog.current.addEventListener('click', handleOutsideClick, controller)

            // Stripe is stealing the show and doesn’t support top-layer, yet
            // Listen to the #modals element changing `tabindex` attribute value
            // and temporarily toggle the dialog open state

            // Related: https://github.com/stripe/stripe-js/issues/599
            const modals = document.getElementById('modals')

            const observer = new MutationObserver(mutations => {
                for(const mutation of mutations) {
                    if(mutation.attributeName === 'tabindex') {
                        const value = modals.getAttribute('tabindex')

                        if(value === '-1' && dialog.current?.open) {
                            dialog.current._temporarilyHidden = true
                            dialog.current.close()
                        } else if(value !== '-1' && dialog.current._temporarilyHidden && !dialog.current?.open) {
                            dialog.current._temporarilyHidden = false
                            dialog.current.showModal()
                        }
                    }
                }
            })

            observer.observe(modals, {
                attributes: true,
                attributeFilter: ['tabindex']
            })

            return () => {
                controller.abort()
                observer.disconnect()
            }
        }
    }, [handleOpen, handleOutsideClick, handleClose])

    useEffect(() => {
        if(!!show) {
            handleOpen()
        } else if(dialog.current?.open) {
            handleClose()
        }
    }, [show, handleOpen, handleClose])

    // Prevent closing with escape key if dirty
    useEffect(() => {
        if(dirty && show) {
            dialog.current.addEventListener('keydown', e => {
                if(e.key === 'Escape') {
                    e.preventDefault()
                    handleClose()
                }
            })
        }
    }, [dirty, show, handleClose])

    return modalify(
        <ModalContext.Provider value={{
            show,
            showing,
            setOptions: options => setOptions(previousOptions => ({
                ...previousOptions,
                ...options
            })),
            dismiss: handleClose,
            dirty,
            setDirty,
            ref: dialog
        }}>
            <FocusLock
                {...modal}
                {...options}
                as={Wrapper}
                group="modal"
                returnFocus
                disabled={!show}
                lockProps={{
                    style: {
                        '--__huma-component-modal-open-duration': `${duration[0]}ms`,
                        '--__huma-component-modal-close-duration': `${duration?.[1] ?? duration[0]}ms`
                    }
                }}
                ref={dialog}
                key={`modal:dialog:${salt}`}>
                {!!show && (
                    <Container>
                        <ModalErrorBoundary
                            dismiss={handleClose}
                            key={`modal:${salt}`}>
                            <ContentWrapper {...(contentClassName ? { className: contentClassName } : null)}>
                                {children}
                            </ContentWrapper>
                        </ModalErrorBoundary>
                        {dirty && (
                            <Prompt
                                show={prompting}
                                question={formatMessage({
                                    id: 'prompt_changes',
                                    defaultMessage: 'Your changes will be lost if you proceed.'
                                })}
                                cancel={{ callback: () => setPrompting(false) }}
                                confirm={{
                                    text: formatMessage({
                                        id: 'action_close',
                                        defaultMessage: 'Close'
                                    }),
                                    callback: () => {
                                        setPrompting(false)
                                        handleClose({ force: true })
                                    }
                                }} />
                        )}
                    </Container>
                )}
            </FocusLock>
        </ModalContext.Provider>
    )
}

Modal.Header = forwardRef(({
    symbol = null,
    kicker = '',
    heading = '', size = 'big', headingClassName,
    caption = '',
    children, childrenClassName,
    showDismiss = true, flipped = false, className,
    ...props
}, ref) => {
    const { dismiss } = useModal()

    const Heading = {
        huge: Huge,
        big: Big,
        medium: Medium,
        small: Small
    }[size]

    return (
        <ModalHeader
            {...props}
            className={cls([
                className,
                flipped && 'flipped',
                'modal-header'
            ])}
            ref={ref}>
            {(!!showDismiss && dismiss) && (
                <CloseButton
                    onClick={dismiss}
                    className="close" />
            )}
            {symbol && (
                <Symbol
                    {...symbol}
                    className="modal-header-symbol" />
            )}
            <TextOrCustomElement
                value={kicker}
                textComponent={Kicker}
                composeProps={props => ({
                    className: cls([
                        props.className,
                        'modal-header-kicker'
                    ])
                })} />
            {!!heading && (
                <Heading className={cls([
                    'modal-header-heading',
                    headingClassName
                ])}>
                    {heading}
                </Heading>
            )}
            <TextOrCustomElement
                value={caption}
                textComponent={Caption}
                composeProps={props => ({
                    className: cls([
                        props.className,
                        'modal-header-caption caption compact'
                    ])
                })} />
            {children && (
                <div className={cls([
                    'modal-header-children',
                    childrenClassName
                ])}>
                    {children}
                </div>
            )}
        </ModalHeader>
    )
})

Modal.Body = ({ children, className, ...props }) => (
    <ModalBody
        {...props}
        className={cls([
            className,
            'modal-body'
        ])}>
        {children}
    </ModalBody>
)

Modal.Footer = ({ children, className, ...props }) => (
    <ModalFooter
        {...props}
        className={cls([
            className,
            'modal-footer'
        ])}>
        {children}
    </ModalFooter>
)

Modal.Loader = () => (
    <LoadingContainer>
        <Loader />
    </LoadingContainer>
)

export const useModal = () => useContext(ModalContext)
export default Modal